// Singleton de comunicação com a API — centraliza todas as requisições HTTP ao backend Flask
// Uso: import Api from './services/Api'; Api.login(email, password);
import {
  API_BASE_URL,
  API_UPLOAD_FALECIDOS,
  API_UPLOAD_JAZIGOS,
  API_UPLOAD_DOCUMENTS,
  API_UPLOAD_DIRECTORY,
  API_DOCUMENTS_FOLDER,
  API_JAZIGOS_FOLDER,
  API_FALECIDOS_FOLDER,
  API_VIDEO_FOLDER
} from "../constants";

class ApiService {
  // Token JWT do usuário autenticado — injetado automaticamente nas requisições protegidas
  token = null;
  // Callback chamado automaticamente quando a API retorna 401 (token expirado)
  onUnauthorized = null;

  /** Define o token JWT após o login bem-sucedido. */
  setToken(token) {
    this.token = token;
  }

  /** Remove o token JWT ao deslogar. */
  clearToken() {
    this.token = null;
  }

  /** Registra o callback a ser chamado quando o token expirar (401). */
  setOnUnauthorized(fn) {
    this.onUnauthorized = fn;
  }

  /**
   * Método base para todas as requisições HTTP.
   * Injeta automaticamente o header Authorization quando o token está definido.
   * @param {string} path - caminho da rota, ex: "/api/auth/login"
   * @param {RequestInit} options - opções do fetch (method, body, headers extras)
   * @returns {Promise<any>} JSON da resposta
   * @throws {Error} com a mensagem de erro retornada pela API
   */
  async request(path, options = {}) {
    const headers = { "Content-Type": "application/json", ...options.headers };
    if (!headers["Authorization"]) {
      const token =
        this.token || JSON.parse(localStorage.getItem("user"))?.token || null;
      if (token) headers["Authorization"] = `Bearer ${token}`;
    }
    const res = await fetch(`${API_BASE_URL}${path}`, { ...options, headers });
    const text = await res.text();
    let data = null;
    try {
      data = JSON.parse(text);
    } catch {}

    if (res.status === 401) {
      const hadToken = !!this.token;
      this.clearToken();
      if (hadToken && this.onUnauthorized) this.onUnauthorized();
      throw new Error(
        data?.message ||
          data?.msg ||
          data?.error ||
          "Sessão expirada. Faça login novamente.",
      );
    }
    if (!res.ok) {
      const msg =
        data?.message ||
        data?.msg ||
        data?.error ||
        text
          .match(/(?:ValueError|Error|Exception):\s*([^\n<]+)/)?.[1]
          ?.trim() ||
        `Erro ${res.status}`;
      throw new Error(msg);
    }
    return data;
  }

  // ── Autenticação ────────────────────────────────────────────────────────────

  /**
   * Autentica o usuário com email e senha.
   * @param {string} email
   * @param {string} password
   * @returns {Promise<{access_token: string}>}
   * @throws {Error} se as credenciais forem inválidas (401)
   */
  login(email, password) {
    return this.request("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
  }

  /**
   * Retorna os dados do usuário autenticado.
   * Deve ser chamado logo após login(), passando o token recém-recebido.
   * @param {string} token - JWT recebido em login()
   * @returns {Promise<{name: string, username: string, email: string}>}
   */
  me(token) {
    return this.request("/api/auth/me", {
      headers: { Authorization: `Bearer ${token}` },
    });
  }

  /**
   * Registra um novo usuário no sistema.
   * @param {{ name: string, cpf: string, email: string, username: string, password: string }} data
   * @returns {Promise<{msg: string}>}
   * @throws {Error} se email, CPF ou nome de usuário já estiverem em uso
   */
  register(data) {
    return this.request("/api/auth/register", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  changePassword(data){
    return this.request("/api/auth/password", {
      method: "PATCH",
      body: JSON.stringify(data),
    })
  }

  getUsers() {
    return this.request("/api/auth/users");
  }

  changeUsersStatus(data) {
    return this.request(`/api/auth/active_users`, {
      method: "PATCH",
      body: JSON.stringify({ users: data }),
    });
  }

  // ── Falecidos ───────────────────────────────────────────────────────────────

  /**
   * Retorna a lista completa de falecidos cadastrados.
   * Não exige autenticação.
   * @returns {Promise<Array>}
   */
  getFalecidos() {
    return this.request("/api/falecidos/");
  }

  async getFalecidosPorId(id) {
    return this.request(`/api/falecidos/${id}`);
  }

  getFalecidosPorNome(nome) {
    return this.request(`/api/falecidos/?nome=${encodeURIComponent(nome)}`);
  }

  getFalecidosPorData(data) {
    return this.request(
      `/api/falecidos/?data_falecimento=${encodeURIComponent(data)}`,
    );
  }

  getFalecidosPorApelido(apelido) {
    return this.request(
      `/api/falecidos/?apelido=${encodeURIComponent(apelido)}`,
    );
  }

  getFalecidosPorNomePai(nome_pai) {
    return this.request(
      `/api/falecidos/?nome_pai=${encodeURIComponent(nome_pai)}`,
    );
  }

  getFalecidosPorNomeMae(nome_mae) {
    return this.request(
      `/api/falecidos/?nome_mae=${encodeURIComponent(nome_mae)}`,
    );
  }

  async getJazigosSetor(setor) {
    let jsonResponse = await this.request(`/api/jazigos/?quadra=${setor}`);
    return jsonResponse;
  }

  // ── Sepultamentos / Jazigos ─────────────────────────────────────────────────

  /**
   * Retorna os sepultamentos de um falecido específico.
   * @param {number} falecidoId
   * @returns {Promise<Array<{id, falecido_id, jazigo_id, data_sepultamento, situacao}>>}
   */
  getSepultamentosByFalecido(falecidoId) {
    return this.request(`/api/sepultamentos/falecido/${falecidoId}`);
  }

  /**
   * Retorna os documentos anexados a um falecido específico.
   * @param {number} falecidoId
   * @returns {Promise<Array<{id, falecido_id, tipo, arquivo_path, descricao}>>}
   */
  getFalecidoDocumentos(falecidoId) {
    return this.request(`/api/falecidos_documentos/falecidos/${falecidoId}`);
  }

  async uploadFalecidoFoto(falecidoId, file) {
    const formData = new FormData();
    formData.append("file", file);
    const token =
      this.token || JSON.parse(localStorage.getItem("user"))?.token || null;
    const res = await fetch(`${API_UPLOAD_FALECIDOS}/${falecidoId}/foto`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    if (res.status === 401) {
      const hadToken = !!this.token;
      this.clearToken();
      if (hadToken && this.onUnauthorized) this.onUnauthorized();
      throw new Error("Sessão expirada. Faça login novamente.");
    }
    if (!res.ok) throw new Error("Erro no upload da foto do falecido");
    return res.json();
  }

  async uploadFalecidoDoc(falecidoId, file, tipo = "Outro documento", descricao = "") {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("falecido_id", falecidoId);
    formData.append("tipo", tipo);
    if (descricao) formData.append("descricao", descricao);
    const token =
      this.token || JSON.parse(localStorage.getItem("user"))?.token || null;
    const res = await fetch(`${API_UPLOAD_DOCUMENTS}`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    if (res.status === 401) {
      const hadToken = !!this.token;
      this.clearToken();
      if (hadToken && this.onUnauthorized) this.onUnauthorized();
      throw new Error("Sessão expirada. Faça login novamente.");
    }
    if (!res.ok) throw new Error("Erro no upload do documento do falecido");
    return res.json();
  }

  uploadMessage(formData, onProgress) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", API_UPLOAD_DIRECTORY + "/message");
      if (this.token) xhr.setRequestHeader("Authorization", `Bearer ${this.token}`);
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) onProgress?.(Math.round((event.loaded * 100) / event.total));
      };
      xhr.onload = () => {
        try { resolve(JSON.parse(xhr.responseText)); } catch { resolve({}); }
      };
      xhr.onerror = () => reject(new Error("Erro no upload da mensagem"));
      xhr.send(formData);
    });
  }
  /**
   * Retorna todos os jazigos como GeoJSON FeatureCollection.
   * Cada feature tem properties.id e properties.codigo.
   * @returns {Promise<{type: "FeatureCollection", features: Array}>}
   */
  getJazigos() {
    return this.request("/api/jazigos/");
  }

  /**
   * Retorna os dados de um jazigo pelo ID.
   * @param {number} jazigoId
   * @returns {Promise<{id, codigo, quadra, lote, numero, tipo, status}>}
   */
  getJazigo(jazigoId) {
    return this.request(`/api/jazigos/${jazigoId}`);
  }

  /**
   * Retorna os falecidos ativos sepultados em um jazigo.
   * @param {number} jazigoId
   * @returns {Promise<Array>}
   */
  getJazigoFalecidosAtivos(jazigoId) {
    return this.request(`/api/jazigos/${jazigoId}/falecidos-ativos`);
  }

  /**
   * Retorna as fotos de um jazigo pelo ID do jazigo.
   * @param {number} jazigoId
   * @returns {Promise<Array<{id, jazigo_id, tipo, arquivo_path, descricao}>>}
   */
  getJazigoFotos(jazigoId) {
    return this.request(`/api/jazigos_fotos/jazigos/${jazigoId}`);
  }

  /**
   * Envia uma nova foto para um jazigo.
   * Requer autenticação JWT.
   * @param {number} jazigoId
   * @param {File} file
   * @param {string} [tipo]
   * @param {string} [descricao]
   * @returns {Promise<{mensagem, foto_id, arquivo}>}
   */
  async uploadJazigoFoto(jazigoId, file, tipo = "", descricao = "") {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("jazigo_id", jazigoId);
    if (tipo) formData.append("tipo", tipo);
    if (descricao) formData.append("descricao", descricao);
    const token =
      this.token || JSON.parse(localStorage.getItem("user"))?.token || null;
    const res = await fetch(API_UPLOAD_JAZIGOS, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    if (res.status === 401) {
      const hadToken = !!this.token;
      this.clearToken();
      if (hadToken && this.onUnauthorized) this.onUnauthorized();
      throw new Error("Sessão expirada. Faça login novamente.");
    }
    if (!res.ok) throw new Error("Erro no upload da foto do jazigo");
    return res.json();
  }

  /**
   * Exclui uma foto de jazigo pelo ID da foto.
   * Requer autenticação JWT.
   * @param {number} fotoId
   * @returns {Promise<{mensagem}>}
   */
  deleteJazigoFoto(fotoId) {
    return this.request(`/api/upload/jazigo_image/${fotoId}`, {
      method: "DELETE",
    });
  }

  // ── Cadastro ────────────────────────────────────────────────────────────────

  /**
   * Cadastra um novo jazigo.
   * Requer autenticação JWT.
   * @param {{ codigo, quadra, lote, numero, tipo, status, capacidade, responsavel, telefone_responsavel, publico, observacoes, geom }} data
   * @returns {Promise<any>}
   */
  createJazigo(data) {
    return this.request("/api/jazigos/", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  /**
   * Cadastra um novo falecido.
   * Requer autenticação JWT.
   * @param {{ nome_completo, data_nascimento, data_falecimento, nome_pai, nome_mae, apelido, registrado, mensagem_familia, observacoes }} data
   * @returns {Promise<any>}
   */
  createBuried(data) {
    return this.request("/api/falecidos/", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  updateBuried(id, data) {
    return this.request(`/api/falecidos/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  updateJazigo(id, data) {
    return this.request(`/api/jazigos/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  updateSepultamento(id, data) {
    return this.request(`/api/sepultamentos/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  /**
   * Cadastra um novo sepultamento.
   * Requer autenticação JWT.
   * @param {{ falecido_id, jazigo_id, data_sepultamento, responsavel_sepultamento, ordem_no_jazigo }} data
   * @returns {Promise<any>}
   */
  createBurial(data) {
    return this.request("/api/sepultamentos/", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  // ── Upload de fotos e mensagens ─────────────────────────────────────────────

  getFalecidoFotoUrl(falecidoImage) {
    return `${API_FALECIDOS_FOLDER}/${falecidoImage}`;
  }

  getFalecidoVideoUrl(falecidoVideo) {
    return `${API_VIDEO_FOLDER}/${falecidoVideo}`;
  }

  getJazigoFotosUrl(jazigoImage) {
    return `${API_JAZIGOS_FOLDER}/${jazigoImage}`;
  }

  getFalecidoDocumentUrl(documentPath) {
    return `${API_DOCUMENTS_FOLDER}/${documentPath}`;
  }

  getMessageImageUrl(messageImage) {
    return `${API_FALECIDOS_FOLDER}/${messageImage}`;
  }

  // ------------- Recuperação de mensagens para memorial --------------
  getMensagens() {
    return this.request("/api/messages/");
  }

  approveMessage(id) {
    return this.request(`/api/messages/${id}/approved`, {
      method: "PUT",
    });
  }

  reproveMessage(id) {
    return this.request(`/api/messages/${id}/reproved`, {
      method: "PUT",
    });
  }

  solicitarCorrecao(formData) {
    const token =
      this.token || JSON.parse(localStorage.getItem("user"))?.token || null;
    const headers = {};
    if (token) headers["Authorization"] = `Bearer ${token}`;
    return fetch(`${API_BASE_URL}/api/correcoes/`, {
      method: "POST",
      headers,
      body: formData,
    }).then(async (res) => {
      const text = await res.text();
      if (!res.ok) throw new Error(text);
      return text ? JSON.parse(text) : {};
    });
  }

  getCorrecoes() {
    return this.request("/api/correcoes/");
  }

  resolverCorrecao(id, status) {
    return this.request(`/api/correcoes/${id}`, {
      method: "PUT",
      body: JSON.stringify({ status }),
    });
  }
}

const Api = new ApiService();

export default Api;
